<template lang="pug">
v-container.disabled-alternate
  v-layout(row wrap)
    v-flex(xs12 color="primary")
      v-text-field(
        data-cy="video_library_text_field"
        label='Search by Video Name or Description'
        solo
        hide-details
        prepend-inner-icon="search"
        v-model="searchVideos"
      )

    //- SubHeaders
    v-flex.title(x12 class="text-xs-right")
      v-btn(
        v-if="isSortedDescending"
        flat
        @click="handleSort"
      ).text-none
        | Created
        v-icon(right dark small).ml-2 arrow_downward

      v-btn(
        v-else
        flat
        @click="handleSort"
      ).text-none
        | Created
        v-icon(right dark small).ml-2 arrow_upward

    //- Loop through available videos
    v-flex(v-for="item in videos" :key="item.id" xs12)
        v-hover
          v-card.pa-3(
            color="grey lighten-5"
            slot-scope="{ hover }"
            :class="`elevation-${hover ? 4 : 2}`"
            flat
            data-cy="video_card_wrapper"
          )
            v-layout.ma-0(row)
              div.video-thumb(
                @click="handleShowVideoPreview(item)"
                v-if="evaluateVideoStatus(item) === 'COMPLETE'"
              )
                img(
                  :src="item.thumbnail_url" :alt="item.title"
                  :title="item.title"
                )
                v-icon play_arrow

              div.video-thumb.missing-video(
                v-else-if="evaluateVideoStatus(item) === 'PROCESSING'"
              )
                v-icon loop
                .thumb-caption
                  | Processing video
                  br
                  | please wait...

              div.video-thumb.missing-video(
                v-else-if="evaluateVideoStatus(item) === 'FAILED'"
                :title="item.title"
              )
                v-icon broken_image
                .thumb-caption(v-if="evaluateVideoStatus(item) === 'FAILED'") Processing Error

              v-layout.ma-0.title-section
                v-flex(xs9)
                  a(
                    @click="handleShowVideoPreview(item)"
                    data-cy="video_title"
                  ).title.text-truncate {{ item.title }}

                v-flex.text-xs-right(xs3)
                  | {{ item.created_at | convertDate }}

            v-layout(row wrap)
              //-- Description
              v-flex(xs12)
                div.body-2.grey--text.py-2.descriptionDiv
                  |  {{ item.description }}
            v-layout(row)
              //-- Tags and Buttons
              v-flex(xs12 md6)
                tag(:data="item.tags")
              v-flex.bottom-row(xs12 md6)
                v-layout(v-if="hover" align-center justify-end)
                  ld-gated-button(
                    v-if="communicationType === 'text'"
                    :disabled="item.status !== 'COMPLETE'"
                    @click="handleInsertVideo(item)"
                    color="primary"
                    small
                    data-cy="video_button_select"
                    :feature="LdFeatures.VideoTexts"
                  ) Select
                  v-btn(
                    v-else
                    :disabled="item.status !== 'COMPLETE'"
                    @click="handleInsertVideo(item)"
                    color="primary"
                    small
                  ) Select
                  v-tooltip(bottom)
                    span Edit Video
                    v-btn(icon slot="activator" @click="handleEditableVideo(item)" )
                      v-icon(color="grey") edit

                  v-tooltip(bottom)
                    span Delete Video
                    v-btn(
                      icon
                      slot="activator"
                      @click="handleRemoveResourceModal('delete', 'single', item)"
                    )
                      v-icon(color="grey") delete

  v-layout.pagination
    v-flex.pages
      v-pagination(
        data-cy-field-name="video_library_pagination"
        :length="paginationPageCount(totalItems, videoLibraryPageSize)"
        total-visible="7"
        v-model="paginationPage"
        v-show="paginationPageCount(totalItems, videoLibraryPageSize) > 1"
      )
    v-flex(style="position:relative")
      pagination-limit.limits(
        data-cy-field-name="video_library_pagination_limit"
        :limits="paginationLimits"
        :current-limit="videoLibraryPageSize"
        @change:limit="loadWithNewPageLimit($event, 'videoLibraryPageSize')"
      )
  remove-resource-modal(
    v-model="removeVideoDialog"
    :action="removeAction"
    resource-singular="video"
    resource-plural="videos"
    :remove-amount="removeAmount"
    @close="onRemoveResourceModalClose"
  )
</template>

<script>
import { convertDate } from '@/Core/filters/dates';
import { debounce } from '@/Core/utilities';
import { createNamespacedHelpers } from 'vuex';
import Media from '@/Media/mixins/media';
import Pagination from '@/Core/mixins/pagination';
import PaginationLimit from '@/Core/components/PaginationLimit';
import RemoveResourceModal from '@/Tasks/components/RemoveResourceModal';
import Tag from '@/Contacts/components/Tag';
import { LdGatedButton, LdFeatures } from 'lion-vue';

const SortOrder = Object.freeze({
  Descending: -1,
  Ascending: 1,
});

const { mapState: mapPreferencesState } = createNamespacedHelpers('preferences');
const {
  mapState: mapVideosState,
  mapActions: mapVideosActions,
} = createNamespacedHelpers('videos');


export default {
  components: {
    PaginationLimit,
    RemoveResourceModal,
    Tag,
    LdGatedButton,
  },
  props: {
    communicationType: {
      type: String,
    },
  },
  data() {
    return {
      LdFeatures,
      searchVideos: null,
      isSortedDescending: true,
      removeAction: 'delete',
      removeAmount: 'single',
      removeVideoDialog: false,
      removeVideoObj: null,
    };
  },
  async mounted() {
    this.queryObj.communication_type = this.communicationType;

    await this.findVideos(this.queryObj);
  },
  computed: {
    ...mapPreferencesState(['videoLibraryPageSize']),
    ...mapVideosState([
      'inProgress',
      'mediaError',
      'mediaErrorMessage',
      'totalItems',
      'videos',
    ]),
    queryObj() {
      const query =
      {
        $skip: (this.paginationGetPage - 1) * this.videoLibraryPageSize,
        $limit: this.videoLibraryPageSize,
        $sort: { created_at: this.isSortedDescending ? SortOrder.Descending : SortOrder.Ascending },
        communication_type: this.communicationType,
      };
      const searchTerm = this.searchVideos;
      if (searchTerm) {
        query.$or = [
          { title: { $like: `%${searchTerm}%` } },
          { description: { $like: `%${searchTerm}%` } },
        ];
      }
      return query;
    },
  },
  filters: {
    convertDate,
  },
  methods: {
    ...mapVideosActions(['deleteVideo', 'findVideos']),
    handleSort() {
      // toggle sort direction
      this.isSortedDescending = !this.isSortedDescending;

      // reset to first page
      this.queryObj.$skip = 0;

      this.findVideos(this.queryObj);
    },
    handleEditableVideo(videoObj) {
      // true is used to set "isVideoEditable" state to true;
      this.$emit('edit-ld-video', videoObj, true);
    },
    handleInsertVideo(videoObj) {
      this.$emit('insert-ld-video', videoObj);
    },
    handleShowVideoPreview(videoObj) {
      this.$emit('click-ld-video', videoObj);
    },
    handleRemoveResourceModal(action, amount, videoObj = null) {
      this.removeVideoDialog = true;
      this.removeVideoObj = videoObj;
      this.removeAction = action;
      this.removeAmount = amount;
    },
    async onRemoveResourceModalClose(isResourceRemoved) {
      this.removeVideoDialog = false;
      if (isResourceRemoved) {
        await this.deleteVideo(({ video: this.removeVideoObj }));
        await this.findVideos(this.queryObj);

        this.removeVideoObj = null;
      }
    },
    reset() {
      this.searchVideos = '';
      this.paginationPage = 1;
    },
  },
  mixins: [
    Media,
    Pagination,
  ],
  watch: {
    videoLibraryPageSize() {
      this.findVideos(this.queryObj);
    },
    paginationPage(value) {
      this.findVideos(this.queryObj);
    },
    searchVideos(value) {
      debounce(this.findVideos, [this.queryObj]);
    },
  },
};
</script>

<style lang="scss" scoped>
  .video-thumb {
    background-color: black;
    height: 72px;
    margin-right: 12px;
    position: relative;
    width: 128px;
    text-align: center;
    img {
      height: 100%;
      vertical-align: middle;
    }
    .v-icon {
      background: white;
      border-radius: 50%;
      cursor: pointer;
      font-size: 24px;
      left: 50%;
      position: absolute;
      top: 50%;
      margin: -12px 0 0 -12px;
    }
  }
  .missing-video {
    height: 72px;
    background: var(--v-background-lighten2);
    .v-icon {
      background: transparent;
    }
  }
  .thumb-caption {
    position: absolute;
    bottom: 0px;
    font-size: 10px;
    width: 100%;
    line-height: 3.65;
  }
  .title-section {
    width: 300px;
  }
  .bottom-row {
    height: 60px;
  }
</style>
